import React, { memo, useMemo, useCallback ,useState} from "react";
import { TableRow, TableCell, Chip ,Popover, Box, Typography, CircularProgress} from "@mui/material";
import dayjs from "dayjs";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { normalizeBaseUrl } from "./attendanceUtils";
import {
  getTotalHours,
  getCellHours,
  getDayKeyFromIso,
  scheduleMinutes,
  formatSignedHours,
  normalizeSch,
  renderCellTextStyled,
  shouldShowDayCellByFilters,
  renderStatus,
  isWeekendIso,
} from "./attendanceUtils";

import axios from "axios";

const EmployeeSection = memo(function EmployeeSection({
  empKey,
  emp,
  visibleDays,
  hideRulesInfos,
  selectedLeaves,
  selectedTimingFilter,
  leaveOptionsCache,
  restLeavesByEmp,
  onCellDoubleClick,
  reportSettings,
}) {
  const LEAVE_ABBR = useMemo(
    () => ({
      Annual: "AN",
      "Sickness Paid": "SP",
      Sick: "SK",
      "Sickness Unpaid": "SU",
      Casual: "CA",
      Emergency: "EM",
      Unpaid: "UP",
      Maternity: "MT",
      Paternity: "PT",
      Compensatory: "CO",
      Marriage: "MR",
      Hajj: "HJ",
      Study: "ST",
      Official: "OF",
    }),
    [],
  );

  const getLeaveAbbreviation = useMemo(() => {
    return (leaveName) => {
      const raw = String(leaveName || "").trim();
      if (!raw) return "";

      const directMatch = Object.keys(LEAVE_ABBR).find(
        (k) => k.toLowerCase() === raw.toLowerCase(),
      );
      if (directMatch) return LEAVE_ABBR[directMatch];

      const match = (leaveOptionsCache || []).find(
        (x) => String(x?.label || "").trim().toLowerCase() === raw.toLowerCase(),
      );

      if (match?.shortName) return String(match.shortName).trim();
      if (match?.abbr) return String(match.abbr).trim();
      if (match?.code) return String(match.code).trim();

      const words = raw.split(/\s+/).filter(Boolean);
      if (words.length >= 2) {
        return words.map((w) => w[0]).join("").toUpperCase();
      }

      return raw.slice(0, 2).toUpperCase();
    };
  }, [LEAVE_ABBR, leaveOptionsCache]);

  const cleanCellText = useMemo(() => {
    return (text) => {
      let result = String(text || "");

      result = result.replace(/\bworking\s*:\s*/gi, "");
      result = result.replace(/devider_line/gi, "");

      (leaveOptionsCache || []).forEach((opt) => {
        const label = String(opt?.label || "").trim();
        if (!label) return;

        const abbr = getLeaveAbbreviation(label);
        if (!abbr) return;

        const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        result = result.replace(new RegExp(escaped, "gi"), abbr);
      });

      Object.keys(LEAVE_ABBR).forEach((name) => {
        const abbr = LEAVE_ABBR[name];
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        result = result.replace(new RegExp(escaped, "gi"), abbr);
      });

      result = result.replace(/[ \t]+\n/g, "\n");
      result = result.replace(/\n[ \t]+/g, "\n");
      result = result.replace(/[ \t]{2,}/g, " ");
      result = result.replace(/\n{3,}/g, "\n\n");

      return result.trim();
    };
  }, [LEAVE_ABBR, getLeaveAbbreviation, leaveOptionsCache]);

  const totalHours = useMemo(() => {
    return getTotalHours(emp, visibleDays);
  }, [emp, visibleDays]);

  // Build a day-of-week → rulestime fallback from any day that has the data.
  // When the API returns no Filters, useAttendanceData stores only { [dayKey]: scheduleText },
  // so we also collect scheduleText as a universal fallback for uncovered days.
  const rulesByDayKey = useMemo(() => {
    const map = {};
    let anySchedule = "";
    Object.values(emp?.days ?? {}).forEach((dayObj) => {
      if (dayObj?.scheduleText && !anySchedule) anySchedule = dayObj.scheduleText;
      if (!dayObj?.rulestime) return;
      Object.entries(dayObj.rulestime).forEach(([k, v]) => {
        if (v && v !== "—" && !map[k]) map[k] = v;
      });
    });
    // Fill gaps from weeklyRulesMap (comes from api/attendances weeklyRules)
    if (emp?.weeklyRulesMap && typeof emp.weeklyRulesMap === "object") {
      Object.entries(emp.weeklyRulesMap).forEach(([k, v]) => {
        if (v && v !== "—" && !map[k]) map[k] = v;
      });
    }
    if (anySchedule) {
      ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].forEach((k) => {
        if (!map[k]) map[k] = anySchedule;
      });
    }
    return map;
  }, [emp]);

  const getRuleTxt = useCallback(
    (iso) => {
      const dayKey = getDayKeyFromIso(iso);
      const direct = emp?.days?.[iso]?.rulestime?.[dayKey];
      if (direct && direct !== "—") return direct;
      const fallback = rulesByDayKey[dayKey];
      if (fallback && fallback !== "—") return fallback;
      return "—";
    },
    [emp, rulesByDayKey],
  );

  const supposedTotalMin = useMemo(() => {
    return visibleDays.reduce((acc, iso) => {
      const min = scheduleMinutes(getRuleTxt(iso));
      return acc + (min ?? 0);
    }, 0);
  }, [visibleDays, getRuleTxt]);

  const realScheduleTotalMin = useMemo(() => {
    return visibleDays.reduce((acc, iso) => {
      const realTxt = normalizeSch(emp?.days?.[iso]?.sumtimesh ?? "");
      const min = scheduleMinutes(realTxt);
      return acc + (min ?? 0);
    }, 0);
  }, [emp, visibleDays]);

  const additionRetentionTotalMin = useMemo(() => {
    return visibleDays.reduce((acc, iso) => {
      const dayKey = getDayKeyFromIso(iso);
      const dayObj = emp?.days?.[iso];
      const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
      const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";

      const finalMin = scheduleMinutes(realTxt);
      const rulesMin = scheduleMinutes(rulesTxt);
      const diffMin =
        finalMin == null || rulesMin == null ? 0 : finalMin - rulesMin;

      return acc + diffMin;
    }, 0);
  }, [emp, visibleDays]);

  const additionRetentionAfterRulesTotalMin = useMemo(() => {
    return visibleDays.reduce((acc, iso) => {
      return (
        acc + (Number(emp?.days?.[iso]?.scheduleAfterRulesMinutes ?? 0) || 0)
      );
    }, 0);
  }, [emp, visibleDays]);

  const hoursAfterRulesTotalMin = useMemo(() => {
    return visibleDays.reduce((acc, iso) => {
      const dayObj = emp?.days?.[iso] || {};

      const cellHours = getCellHours(dayObj);
      const cellMinutes = Math.round(Number(cellHours || 0) * 60);

      const dayKey = getDayKeyFromIso(iso);
      const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
      const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";

      const finalMin = scheduleMinutes(realTxt);
      const rulesMin = scheduleMinutes(rulesTxt);
      const additionRetentionMinutes =
        finalMin == null || rulesMin == null ? 0 : finalMin - rulesMin;

      const scheduleAfterRulesMinutes =
        Number(dayObj?.scheduleAfterRulesMinutes ?? 0) || 0;

      return (
        acc +
        (cellMinutes - additionRetentionMinutes + scheduleAfterRulesMinutes)
      );
    }, 0);
  }, [emp, visibleDays]);

// 26/5/26
const [gpsPopup, setGpsPopup] = useState({
  open: false, anchor: null, loading: false,
  data: null, error: "", time: null
});

const [stampPopup, setStampPopup] = useState({    // ← add
  open: false, anchor: null, time: null, stampDateTime: null
});

//26/5/26
const handleGpsClick = useCallback(async (e, time, isoDate) => {
  e.stopPropagation();
  setGpsPopup({ open: true, anchor: e.currentTarget, loading: true, data: null, error: "", time });
  try {
    const rawBase = localStorage.getItem("saved_sett_api_url");
    const API_BASE_URL = normalizeBaseUrl(rawBase);
    const res = await axios.get(`${API_BASE_URL}/api/location/address`, {
      params: { empId: emp?.empId, date: isoDate, time },
      withCredentials: true,
    });
    setGpsPopup(p => ({ ...p, loading: false, data: res.data }));
  } catch {
    setGpsPopup(p => ({ ...p, loading: false, error: "Address not found" }));
  }
}, [emp]);

const handleStampClick = useCallback((e, time, stampDateTime) => {   // ← add
  e.stopPropagation();
  setStampPopup({ open: true, anchor: e.currentTarget, time, stampDateTime });
}, []);


  const visibleFieldKeys = useMemo(() => {
    if (!reportSettings) {
      return new Set([
        "attendance",
        "realSchedule",
        "supposedSchedule",
        "workingHours",
        "additionRetentionHours",
        "additionRetentionAfterRules",
        "workingHoursAfterRules",
        "leave",
      ]);
    }

    if (reportSettings.all) {
      return new Set([
        "attendance",
        "realSchedule",
        "supposedSchedule",
        "workingHours",
        "additionRetentionHours",
        "additionRetentionAfterRules",
        "workingHoursAfterRules",
        "leave",
      ]);
    }

    if (reportSettings.attendance) {
      return new Set([
        "attendance",
        "realSchedule",
        "supposedSchedule",
        "workingHours",
        "additionRetentionHours",
        "additionRetentionAfterRules",
        "workingHoursAfterRules",
      ]);
    }

     if (reportSettings.leave) {
      return new Set([
        "attendance",
      "realSchedule",
      "supposedSchedule",
      "workingHours",
      "additionRetentionHours",
      "additionRetentionAfterRules",
      "workingHoursAfterRules",
      ]);
     }

    if (reportSettings.custom) {
      return new Set(reportSettings.customFields || []);
    }

    return new Set([
      "attendance",
      "realSchedule",
      "supposedSchedule",
      "workingHours",
      "additionRetentionHours",
      "additionRetentionAfterRules",
      "workingHoursAfterRules",
    ]);
  }, [reportSettings]);

  const showAttendance = visibleFieldKeys.has("attendance");
  const showRealSchedule = visibleFieldKeys.has("realSchedule");
  const showSupposedSchedule = visibleFieldKeys.has("supposedSchedule");
  const showHours = visibleFieldKeys.has("workingHours");
  const showAdditionRetentionHours = visibleFieldKeys.has("additionRetentionHours");
  const showAdditionRetentionAfterRules = visibleFieldKeys.has("additionRetentionAfterRules");
  const showHoursAfterRules = visibleFieldKeys.has("workingHoursAfterRules");
  const showLeave = visibleFieldKeys.has("leave");

  const hasAnyRuleRows =
  
    !hideRulesInfos &&
    (
      showRealSchedule ||
      showSupposedSchedule ||
      showHours ||
      showAdditionRetentionHours ||
      showAdditionRetentionAfterRules ||
      showHoursAfterRules
    );

  return (
    <>
      {hideRulesInfos && showAttendance ? (
        <>
        {/* // ── Merged row: name + attendance data together, weekday header replaced by data ── */}
        <TableRow>
          <TableCell
            sx={{
              minWidth: 220,
              fontWeight: 800,
              bgcolor: "#e0f2f1",
              color: "#004d40",
              position: "sticky",
              left: 0,
              zIndex: 4,
              
            }}
          >
            {emp?.employee || empKey}
          </TableCell>

          {visibleDays.map((iso) => {
            const cell = emp?.days?.[iso];
            const showThisCell = shouldShowDayCellByFilters(
              cell,
              iso,
              selectedLeaves,
              leaveOptionsCache,
              selectedTimingFilter,
            );

            return (
              <TableCell
                key={iso}
                align="center"
                onDoubleClick={() => onCellDoubleClick(empKey, iso)}
                sx={{
                  cursor: "pointer",
                  verticalAlign: "middle",
                  minWidth: 110,
                  bgcolor: isWeekendIso(iso) ? "rgba(0,0,0,.02)" : "#f7faf9",
                  
                }}
              >
               {showThisCell && String(cell?.time || "").trim()
                  ? renderCellTextStyled(
                      cell?.time,
                      cell?.stampModules,
                      cell?.stampDates,
                      (e, time) => handleGpsClick(e, time, iso),
                      (e, time, stampDateTime) => handleStampClick(e, time, stampDateTime)
                    )
                  : renderStatus(cell?.status)}
              </TableCell>
            );
          })}

          <TableCell align="center" sx={{ minWidth: 100, fontWeight: 800, color: "#004d40" }}>
            {totalHours.toFixed(2)} h
          </TableCell>
        </TableRow>

        <TableRow>
      <TableCell colSpan={visibleDays.length + 2} sx={{ height: 8, p: 0, border: "none" }} />
    </TableRow>

    </>

      ) : (
        <>
          <TableRow>
            <TableCell
              colSpan={visibleDays.length + 2}
              sx={{
                fontWeight: 800,
                bgcolor: "#e0f2f1",
                color: "#004d40",
                p: 0,
              }}
            >
              <Box
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  width: "fit-content",
                  px: 2,
                  py: 1.2,
                }}
              >
                {emp?.employee || empKey}
              </Box>
            </TableCell>
          </TableRow>

      {showAttendance && (
        <TableRow>
          <TableCell
            sx={{
              fontWeight: 800,
              color: "#004d40",
              position: "sticky",
              left: 0,
              zIndex: 3,
              bgcolor: "white",
            }}
          >
            Attendance 
          </TableCell>
          
          {visibleDays.map((iso) => {   // iso = current date in the loop
            // came from: apiDayToGridCell(site + schedule + leave)
            const cell = emp?.days?.[iso];
            const showThisCell = shouldShowDayCellByFilters(
              cell,
              iso,    // iso is a date string , iso = "2026-05-18" 
              selectedLeaves,
              leaveOptionsCache,
              selectedTimingFilter,
            );

            return (
              <TableCell
                key={iso}
                align="center"
                onDoubleClick={() => onCellDoubleClick(empKey, iso)}
                sx={{
                  cursor: "pointer",
                  verticalAlign: "middle",
                  fontWeight: 800,
                  bgcolor: isWeekendIso(iso) ? "rgba(0,0,0,.02)" : "white",
                }}
              >
                {showThisCell && String(cell?.time || "").trim()
                  // the old one : ? renderCellTextStyled(cleanCellText(cell?.time), cell?.stampModules)
                  //? renderCellTextStyled(cell?.time, cell?.stampModules, cell?.stampDates)
                  //26/5/26
                  // for style (color)
                  ? renderCellTextStyled(
                  cell?.time,
                  cell?.stampModules,
                  cell?.stampDates,
                  (e, time) => handleGpsClick(e, time, iso),
                  (e, time, stampDateTime) => handleStampClick(e, time, stampDateTime) 
                )
                  : renderStatus(cell?.status)}
                   {/* if no time → shows status icon */}
              </TableCell>
            );
          })}

          <TableCell align="center" sx={{ fontWeight: 800, color: "#004d40"}}>
            {totalHours.toFixed(2)} h
          </TableCell>
        </TableRow>
      )}

      {hasAnyRuleRows && (
        <>
          {showRealSchedule && (
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 800,
                  color: "#db881a",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "#fff",
                }}
              >
                Real Schedule
              </TableCell>

              {visibleDays.map((iso) => {
                // computed from pschdly ONLY
                const formatted = normalizeSch(emp?.days?.[iso]?.sumtimesh ?? "");
                return (
                  <TableCell key={iso} align="center" sx={{ color: "#db881a",fontWeight: 800, }}>
                    {formatted || "—"}
                     {/* if sumtimesh empty → shows "—" */}
                  </TableCell>
                );
              })}

              <TableCell align="center" sx={{ fontWeight: 800, color: "#db881a" }}>
                {formatSignedHours(realScheduleTotalMin)}
              </TableCell>
            </TableRow>
          )}

          {showSupposedSchedule && (
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 800,
                  color: "#e016d6",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "#fff",
                }}
              >
                Supposed Schedule
              </TableCell>

              {visibleDays.map((iso) => {
                const ruleTxt = getRuleTxt(iso);

                return (
                  <TableCell key={iso} align="center" sx={{ color: "#e016d6",fontWeight: 800 }}>
                    {ruleTxt}
                  </TableCell>
                );
              })}

              <TableCell align="center" sx={{ fontWeight: 800, color: "#e016d6" }}>
                {formatSignedHours(supposedTotalMin)}
              </TableCell>
            </TableRow>
          )}

          {showHours && (
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 800,
                  color: "#607d8b",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "#fff",
                }}
              >
                Hours
              </TableCell>

              {visibleDays.map((iso) => {
                const h = getCellHours(emp?.days?.[iso]);
                return (
                  <TableCell key={iso} align="center" sx={{fontWeight: 800}}>
                    {h > 0 ? h.toFixed(2) : "—"}
                  </TableCell>
                );
              })}

              <TableCell align="center" sx={{ fontWeight: 800, color: "#607d8b" }}>
                {totalHours.toFixed(2)}
              </TableCell>
            </TableRow>
          )}

          {showAdditionRetentionHours && (
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 800,
                  color: "#263238",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "#fff",
                }}
              >
                Addition/Retention Hours
              </TableCell>

              {visibleDays.map((iso) => {
                const dayKey = getDayKeyFromIso(iso);
                const dayObj = emp?.days?.[iso];
                const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
                const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";

                const finalMin = scheduleMinutes(realTxt);
                const rulesMin = scheduleMinutes(rulesTxt);
                const diffMin =
                  finalMin == null || rulesMin == null ? null : finalMin - rulesMin;

                const color =
                  diffMin == null
                    ? "#263238"
                    : diffMin > 0
                      ? "#0b7a4b"
                      : diffMin < 0
                        ? "#b71c1c"
                        : "#455a64";

                return (
                  <TableCell
                    key={iso}
                    align="center"
                    sx={{ color, fontWeight: 800 }}
                  >
                    {formatSignedHours(diffMin)}
                  </TableCell>
                );
              })}

              <TableCell
                align="center"
                sx={{
                  fontWeight: 800,
                  color:
                    additionRetentionTotalMin > 0
                      ? "#0b7a4b"
                      : additionRetentionTotalMin < 0
                        ? "#b71c1c"
                        : "#455a64",
                }}
              >
                {formatSignedHours(additionRetentionTotalMin)}
              </TableCell>
            </TableRow>
          )}

          {showAdditionRetentionAfterRules && (
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 800,
                  color: "#1565c0",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "#fff",
                }}
              >
                Addition / Retention After Rules
              </TableCell>

              {visibleDays.map((iso) => {
                const mins =
                  Number(emp?.days?.[iso]?.scheduleAfterRulesMinutes ?? 0) || 0;

                return (
                  <TableCell
                    key={iso}
                    align="center"
                    sx={{ color: "#1565c0", fontWeight: 800 }}
                  >
                    {mins ? `${(mins / 60).toFixed(2)} h` : "—"}
                  </TableCell>
                );
              })}

              <TableCell
                align="center"
                sx={{
                  fontWeight: 800,
                  color: "#1565c0",
                }}
              >
                {additionRetentionAfterRulesTotalMin
                  ? `${(additionRetentionAfterRulesTotalMin / 60).toFixed(2)} h`
                  : "—"}
              </TableCell>
            </TableRow>
          )}

          {showHoursAfterRules && (
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 800,
                  color: "#2e7d32",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  bgcolor: "#fff",
                }}
              >
                Hours After Rules
              </TableCell>

              {visibleDays.map((iso) => {
                const dayObj = emp?.days?.[iso] || {};

                const cellHours = getCellHours(dayObj);
                const cellMinutes = Math.round(Number(cellHours || 0) * 60);

                const dayKey = getDayKeyFromIso(iso);
                const realTxt = normalizeSch(dayObj?.sumtimesh ?? "");
                const rulesTxt = dayObj?.rulestime?.[dayKey] ?? "—";

                const finalMin = scheduleMinutes(realTxt);
                const rulesMin = scheduleMinutes(rulesTxt);
                const additionRetentionMinutes =
                  finalMin == null || rulesMin == null ? 0 : finalMin - rulesMin;

                const scheduleAfterRulesMinutes =
                  Number(dayObj?.scheduleAfterRulesMinutes ?? 0) || 0;

                const result =
                  cellMinutes - additionRetentionMinutes + scheduleAfterRulesMinutes;

                return (
                  <TableCell
                    key={iso}
                    align="center"
                    sx={{
                      color: "#2e7d32",
                      fontWeight: 800,
                    }}
                  >
                    {result ? `${(result / 60).toFixed(2)} h` : "—"}
                  </TableCell>
                );
              })}

              <TableCell
                align="center"
                sx={{
                  fontWeight: 800,
                  color: "#2e7d32",
                }}
              >
                {hoursAfterRulesTotalMin
                  ? `${(hoursAfterRulesTotalMin / 60).toFixed(2)} h`
                  : "—"}
              </TableCell>
            </TableRow>
          )}
        </>
      )}

      {showLeave &&
      // leave balance data that only gets loaded after you click "Calculate Rules" button
       !!restLeavesByEmp?.[String(emp?.empId ?? empKey)]?.items?.length && (
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 800,
                color: "#6a1b9a",
                position: "sticky",
                left: 0,
                zIndex: 3,
                bgcolor: "#fff",
              }}
            >
              Leave
            </TableCell>

            <TableCell colSpan={visibleDays.length + 1}>
              {restLeavesByEmp[String(emp?.empId ?? empKey)].items.map((x) => (
                <Chip
                  key={`${x.leaveid}-${x.leavename}`}
                  label={`${getLeaveAbbreviation(x.leavename) || x.leavename}: ${x.rest_leave_days ?? 0}`}
                  size="small"
                  sx={{
                    mr: 1,
                    mb: 1,
                    fontWeight: 700,
                    bgcolor: "rgba(156,39,176,.09)",
                    color: "#6a1b9a",
                  }}
                />
              ))}
            </TableCell>
          </TableRow>
        )}
      <TableRow>
        <TableCell colSpan={visibleDays.length + 2} sx={{ height: 12, p: 0 }} />
      </TableRow>

        </>
      )}
      {/* 26/5/26 */}
      <Popover
              open={gpsPopup.open}
              anchorEl={gpsPopup.anchor}
              onClose={() => setGpsPopup(p => ({ ...p, open: false }))}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              PaperProps={{ sx: { borderRadius: 2, p: 1.5, maxWidth: 320, boxShadow: 4 } }}
            >
              {gpsPopup.loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography fontSize={13}>Loading...</Typography>
                </Box>
              ) : gpsPopup.error ? (
                <Typography fontSize={13} color="error">{gpsPopup.error}</Typography>
              ) : gpsPopup.data ? (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                    <LocationOnIcon fontSize="small" sx={{ color: "#b71c1c" }} />
                    <Typography fontWeight={900} fontSize={13} color="#b71c1c">
                      GPS — {gpsPopup.time}
                    </Typography>
                  </Box>
                  <Typography fontSize={12} sx={{ mt: 0.5 }}>
                    {gpsPopup.data.address || "—"}
                  </Typography>
                  {gpsPopup.data.nearby && (
                    <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.3 }}>
                      Near: {gpsPopup.data.nearby}
                    </Typography>
                  )}
                  {gpsPopup.data.fullname && (
                    <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.3 }}>
                      {gpsPopup.data.fullname}
                    </Typography>
                  )}
                </Box>
              ) : null}
            </Popover>

            <Popover
              open={stampPopup.open}
              anchorEl={stampPopup.anchor}
              onClose={() => setStampPopup(p => ({ ...p, open: false }))}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              PaperProps={{ sx: { borderRadius: 2, p: 1.5, maxWidth: 280, boxShadow: 4 } }}
            >
              <Typography fontWeight={900} fontSize={13} color="#2e7d32">
                Entered — {stampPopup.time}
              </Typography>
              <Typography fontSize={12} sx={{ mt: 0.5 }}>
                {stampPopup.stampDateTime
                  ? dayjs(stampPopup.stampDateTime.replace(" ", "T")).format("dddd, MMM D [at] h:mm A")
                  : "No stamp info recorded"}
              </Typography>
            </Popover>
    </>
  );
});

export default EmployeeSection;